/**
 * Robust geolocation utility with timeout, fallback strategies, and error handling
 * Designed to work reliably in PWA environments with intermittent connectivity
 */

// Default geolocation options
const DEFAULT_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 5000, // 5 seconds timeout - quick response
  maximumAge: 120000 // 2 minutes cache - frequent updates
};

// Reduced accuracy options for fallback
const FALLBACK_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 3000, // 3 seconds timeout - very quick fallback
  maximumAge: 300000 // 5 minutes cache for low accuracy
};

/**
 * Get current position with timeout and fallback strategies
 * @param {Object} options - Geolocation options
 * @returns {Promise} Promise that resolves with coordinates or rejects with error
 */
export function getCurrentPosition(options = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    const opts = { ...DEFAULT_OPTIONS, ...options };
    let resolved = false;

    // Primary attempt with high accuracy
    const primaryAttempt = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (!resolved) {
            resolved = true;
            console.log('Geolocation: High accuracy position obtained', position);
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              accuracy: position.coords.accuracy,
              timestamp: position.timestamp,
              method: 'high-accuracy'
            });
          }
        },
        (error) => {
          console.warn('Geolocation: High accuracy failed', error);
          if (!resolved) {
            // Try fallback with lower accuracy
            fallbackAttempt();
          }
        },
        opts
      );
    };

    // Fallback attempt with lower accuracy
    const fallbackAttempt = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (!resolved) {
            resolved = true;
            console.log('Geolocation: Fallback position obtained', position);
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              accuracy: position.coords.accuracy,
              timestamp: position.timestamp,
              method: 'fallback'
            });
          }
        },
        (error) => {
          if (!resolved) {
            resolved = true;
            console.error('Geolocation: All attempts failed', error);
            reject(error);
          }
        },
        FALLBACK_OPTIONS
      );
    };

    // Overall timeout as a safety net
    const overallTimeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        console.error('Geolocation: Overall timeout reached');
        reject(new Error('Geolocation timeout: Could not obtain position within time limit'));
      }
    }, opts.timeout + 2000); // 2 seconds extra buffer - reduced from 5

    // Start primary attempt
    primaryAttempt();

    // Clean up timeout if resolved
    const originalResolve = resolve;
    const originalReject = reject;
    
    resolve = (...args) => {
      clearTimeout(overallTimeout);
      originalResolve(...args);
    };
    
    reject = (...args) => {
      clearTimeout(overallTimeout);
      originalReject(...args);
    };
  });
}

/**
 * Get current position with user-friendly error messages
 * @param {Object} options - Geolocation options
 * @returns {Promise} Promise that resolves with coordinates or rejects with user-friendly error
 */
export function getCurrentPositionWithFriendlyErrors(options = {}) {
  return getCurrentPosition(options).catch((error) => {
    let friendlyMessage;
    
    switch (error.code) {
      case error.PERMISSION_DENIED:
        friendlyMessage = 'Location access denied. Please enable location permissions in your browser settings.';
        break;
      case error.POSITION_UNAVAILABLE:
        friendlyMessage = 'Location information is unavailable. Please check your GPS/location services.';
        break;
      case error.TIMEOUT:
        friendlyMessage = 'Location request timed out. Please try again or check your connection.';
        break;
      default:
        friendlyMessage = error.message || 'Unable to retrieve your location. Using default coordinates.';
    }
    
    const friendlyError = new Error(friendlyMessage);
    friendlyError.originalError = error;
    friendlyError.code = error.code;
    
    throw friendlyError;
  });
}

/**
 * Get current position with automatic fallback to (0, 0) coordinates
 * This is the safest method for applications that must continue working without location
 * @param {Object} options - Geolocation options
 * @returns {Promise} Promise that always resolves (never rejects)
 */
export function getCurrentPositionSafe(options = {}) {
  return getCurrentPosition(options)
    .then((coordinates) => {
      return {
        ...coordinates,
        hasLocation: true,
        error: null
      };
    })
    .catch((error) => {
      console.warn('Geolocation failed, using fallback coordinates (0, 0)', error);
      return {
        lat: 0,
        lng: 0,
        accuracy: null,
        timestamp: Date.now(),
        method: 'fallback',
        hasLocation: false,
        error: error.message
      };
    });
}

/**
 * Watch position changes with robust error handling
 * @param {Function} successCallback - Called on successful position updates
 * @param {Function} errorCallback - Called on errors
 * @param {Object} options - Geolocation options
 * @returns {Number} Watch ID that can be used to clear the watch
 */
export function watchPosition(successCallback, errorCallback, options = {}) {
  if (!navigator.geolocation) {
    const error = new Error('Geolocation is not supported by this browser');
    errorCallback(error);
    return null;
  }

  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  const enhancedSuccessCallback = (position) => {
    const coordinates = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      accuracy: position.coords.accuracy,
      timestamp: position.timestamp,
      method: 'watch'
    };
    successCallback(coordinates);
  };

  const enhancedErrorCallback = (error) => {
    console.warn('Geolocation watch error', error);
    
    let friendlyMessage;
    switch (error.code) {
      case error.PERMISSION_DENIED:
        friendlyMessage = 'Location access denied';
        break;
      case error.POSITION_UNAVAILABLE:
        friendlyMessage = 'Location unavailable';
        break;
      case error.TIMEOUT:
        friendlyMessage = 'Location request timed out';
        break;
      default:
        friendlyMessage = 'Location error';
    }
    
    const friendlyError = new Error(friendlyMessage);
    friendlyError.originalError = error;
    friendlyError.code = error.code;
    
    errorCallback(friendlyError);
  };

  return navigator.geolocation.watchPosition(
    enhancedSuccessCallback,
    enhancedErrorCallback,
    opts
  );
}

export default {
  getCurrentPosition,
  getCurrentPositionWithFriendlyErrors,
  getCurrentPositionSafe,
  watchPosition
};
